import React, { Suspense, useEffect, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, Spinner, Center } from '@chakra-ui/react';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { MDXProvider } from '@mdx-js/react';
import MDXComponents from './components/mdx/MDXComponents';
import ErrorBoundary from './components/shared/ErrorBoundary';
import Metadata from './components/Metadata';
import IntercomChat from './components/shared/IntercomChat';

// Lazy load layout components
const Navbar = lazy(() => import('./components/Navbar'));
const Footer = lazy(() => import('./components/layout/Footer'));

// CSS imports are now consolidated in main.jsx

// Lazy load page components
const Home = lazy(() => import('./pages/Home'));
const BlogIndex = lazy(() => import('./pages/blog/index.jsx'));
const BlogPost = lazy(() => import('./pages/blog/post'));
const KnowledgeBase = lazy(() => import('./pages/knowledge-base/index.jsx'));
const PillarPage = lazy(() => import('./pages/knowledge-base/PillarPage'));
const StaticPage = lazy(() => import('./pages/static/Page'));
const SecureYourBusiness = lazy(() => import('./pages/secure-your-business'));
const CyberStrike = lazy(() => import('./pages/cyber-strike'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const Success = lazy(() => import('./pages/Success'));
const AffiliateRegistration = lazy(() => import('./pages/AffiliateRegistration'));

const LoadingSpinner = () => (
  <Center h="100vh">
    <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
  </Center>
);

const KNOWLEDGE_BASE_PILLARS = [
  'sast-static-security',
  'dast-dynamic-security',
  'web-app-penetration',
  'mobile-app-penetration',
  'soc-2-compliance',
  'gdpr-compliance',
  'hipaa-compliance',
  'casa-tier-2-compliance',
  'cybersecurity-audit',
  'managed-cybersecurity',
  'cybersecurity-consulting-services',
  'cybersecurity-for-smbs',
  'dark-web-threat-intelligence',
  'zero-trust-security',
];

const STATIC_PAGES = [
  'terms-conditions',
  'privacy-policy',
  'team',
  'secure-your-business',
  'success',
  'cyber-strike',
  'checkout-cancel',
];

const RouteWrapper = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const path = location.pathname + location.search + location.hash;
    if (typeof window.trackPageview === 'function') {
      setTimeout(() => {
        window.trackPageview(path);
      }, 50);
    }
  }, [location.pathname, location.search, location.hash]);

  return children;
};

function App() {
  // HelmetProvider is still needed for compatibility, but we won't use Helmet directly
  const helmetContext = {};

  return (
    <HelmetProvider context={helmetContext}>
      {/* Helmet removed - all meta tags are now handled by the edge function */}
      <Router>
        <ErrorBoundary>
          <MDXProvider components={MDXComponents}>
            <Metadata />
            <IntercomChat />
            <Box minH="100vh" display="flex" flexDirection="column">
              <Suspense fallback={<Box h="80px" bg="white" />}>
                <Navbar />
              </Suspense>
              <Box flex="1">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<RouteWrapper><Home /></RouteWrapper>} />
                    {/* Redirect all cyber-strike-home and cyber-strike-crew-home to cyber-strike */}
                    <Route path="/cyber-strike-home/" element={<Navigate to="/cyber-strike/" replace />} />
                    <Route path="/cyber-strike-crew-home/" element={<Navigate to="/cyber-strike/" replace />} />
                    <Route path="/blog" element={<Navigate to="/blog/" replace />} />
                    <Route path="/blog/" element={<RouteWrapper><BlogIndex /></RouteWrapper>} />
                    <Route path="/knowledge-base" element={<Navigate to="/knowledge-base/" replace />} />
                    <Route path="/knowledge-base/" element={<RouteWrapper><KnowledgeBase /></RouteWrapper>} />

                    <Route path="/feed/" element={null} />

                    {/* Affiliate Registration Route */}
                    <Route path="/affiliates/" element={<RouteWrapper><AffiliateRegistration /></RouteWrapper>} />
                    <Route path="/affiliates" element={<Navigate to="/affiliates/" replace />} />
                    {/* Legacy redirect */}
                    <Route path="/affiliate-registration/" element={<Navigate to="/affiliates/" replace />} />
                    <Route path="/affiliate-registration" element={<Navigate to="/affiliates/" replace />} />

                    {/* Redirect cyber-scan-free to cyber-strike */}
                    <Route path="/cyber-scan-free/" element={<Navigate to="/cyber-strike/" replace />} />
                    <Route path="/cyber-scan-free" element={<Navigate to="/cyber-strike/" replace />} />

                    {STATIC_PAGES.map((page) => (
                      <React.Fragment key={page}>
                        <Route
                          path={`/${page}/`}
                          element={
                            <RouteWrapper>
                              {page === 'secure-your-business' ? (
                                <SecureYourBusiness />
                              ) : page === 'success' ? (
                                <Success />
                              ) : page === 'cyber-strike' ? (
                                <CyberStrike />
                              ) : page === 'checkout-cancel' ? (
                                <CheckoutCancel />
                              ) : (
                                <StaticPage />
                              )}
                            </RouteWrapper>
                          }
                        />
                        <Route path={`/${page}`} element={<Navigate to={`/${page}/`} replace />} />
                      </React.Fragment>
                    ))}

                    {KNOWLEDGE_BASE_PILLARS.map((pillar) => (
                      <React.Fragment key={pillar}>
                        <Route
                          path={`/${pillar}/`}
                          element={<RouteWrapper><PillarPage /></RouteWrapper>}
                        />
                        <Route path={`/${pillar}`} element={<Navigate to={`/${pillar}/`} replace />} />
                        <Route
                          path={`/${pillar}/:subPage/`}
                          element={<RouteWrapper><PillarPage /></RouteWrapper>}
                        />
                        <Route
                          path={`/${pillar}/:subPage`}
                          element={<Navigate to={({ params }) => `/${pillar}/${params.subPage}/`} replace />}
                        />
                      </React.Fragment>
                    ))}

                    <Route path="/:slug" element={<Navigate to="/:slug/" replace />} />
                    <Route path="/:slug/" element={<RouteWrapper><BlogPost /></RouteWrapper>} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Box>
              <Suspense fallback={<Box h="200px" bg="gray.100" />}>
                <Footer />
              </Suspense>
            </Box>
          </MDXProvider>
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}

export default App;
